import { ArrowRight, ExternalLink, Gauge, Globe2, Layers3, Radio, Waves } from "lucide-react"
import { Link } from "react-router"
import { MetricDashboard } from "@/components/MetricDashboard"
import { Reveal } from "@/components/Reveal"
import { RemoteImage } from "@/components/RemoteImage"
import { SectionHeading } from "@/components/SectionHeading"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { awards, blogPosts, deploymentSites, ecosystemSupport, evidencePhotos, kolonnawaMedia, livePlatformUrl, outcomes, partners, productMedia, products } from "@/data/site"

const deployment = deploymentSites[0]

export default function Home() {
  return (
    <>
      <section className="relative overflow-hidden bg-[#061a1d] px-6 pt-36 text-white sm:pt-40">
        <div className="surface-grid absolute inset-0 opacity-20" />
        <div className="noise absolute inset-0" />
        <div className="mx-auto grid max-w-7xl gap-14 pb-24 lg:grid-cols-[.9fr_1.1fr] lg:items-center sm:pb-32">
          <Reveal className="lg:self-start">
            <div className="relative z-10">
              <Badge className="border border-white/10 bg-white/[.05] text-emerald-200">Smart Drain</Badge>
              <h1 className="mt-7 text-balance text-5xl font-semibold tracking-[-.06em] sm:text-6xl lg:text-7xl">
                <span className="block">Stop litter</span>
                <span className="block">Monitor the flow</span>
                <span className="block">Act before the flood</span>
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-white/70">
                Smart Drain is a retrofit drain filter integrated with IoT monitoring. It captures litter, plastics and debris at the point of entry before they clog stormwater drains. IoT sensors continuously monitor drainage conditions and alert operators to rising water levels, waste accumulation and maintenance needs, enabling earlier intervention before blockages contribute to flooding.
              </p>
              <p className="mt-4 max-w-2xl text-lg leading-8 text-white/70">
                Smart Drain helps protect cities from flooding, keep plastics out of rivers, and reduce council maintenance costs.
              </p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Link to="/start-pilot"><Button size="lg">Start a Pilot <ArrowRight className="size-4" /></Button></Link>
                <a href="#how-it-works"><Button variant="secondary" size="lg">See How Smart Drain Works</Button></a>
              </div>
              <div className="mt-10 flex flex-wrap gap-2 text-sm font-semibold text-white/70">
                {[
                  "INTERCEPT WASTE",
                  "MONITOR THE DRAIN",
                  "DESIGNED FOR CHANGING CITIES & CHANGING CLIMATE",
                ].map((item) => (
                  <span key={item} className="rounded-full border border-white/10 bg-white/[.04] px-4 py-2">{item}</span>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.08}>
            <div className="relative z-10 grid gap-3 sm:grid-cols-2">
              <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[.04] p-2 sm:row-span-2">
                <RemoteImage src={kolonnawaMedia.blockedDrain} alt="Waste-filled urban drain before intervention" className="h-full min-h-[16rem] rounded-[1.5rem]" />
              </div>
              <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[.04] p-2">
                <RemoteImage src={kolonnawaMedia.iotMonitor} alt="IoT monitoring unit installed inside Smart Drain" className="aspect-[4/3] rounded-[1.5rem]" />
              </div>
              <div className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/[.04] p-2">
                <RemoteImage src={productMedia.openDrainStreet} alt="Smart Drain open drain retrofit installed on a street" className="aspect-[4/3] rounded-[1.5rem]" />
              </div>
              <div className="sm:col-span-2">
                <MetricDashboard />
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      <section className="bg-emerald-300 px-6 py-8 text-slate-950 dark:bg-emerald-400/15 dark:text-emerald-100">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-[.8fr_1.2fr_.8fr] md:items-center">
          <div className="text-3xl font-semibold tracking-[-.05em] sm:text-4xl">STOP THE BLOCKAGE BEFORE THE FLOOD!</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl bg-slate-950/8 p-5 dark:bg-white/8"><div className="text-xs font-semibold uppercase tracking-[.16em] text-slate-700 dark:text-emerald-300">INTERCEPT WASTE</div><p className="mt-2 text-sm font-semibold text-slate-800/75 dark:text-white/70">Capture litter and debris before they enter the drainage network.</p></div>
            <div className="rounded-2xl bg-slate-950/8 p-5 dark:bg-white/8"><div className="text-xs font-semibold uppercase tracking-[.16em] text-slate-700 dark:text-emerald-300">MONITOR THE DRAIN</div><p className="mt-2 text-sm font-semibold text-slate-800/75 dark:text-white/70">Real-time data supports inspection, maintenance and early action.</p></div>
          </div>
          <Link to="/start-pilot" className="md:justify-self-end"><Button className="bg-slate-950 text-white hover:bg-slate-800">Pilot Smart Drain in your city <ArrowRight className="size-4" /></Button></Link>
        </div>
      </section>

      <section id="how-it-works" className="px-6 py-24 sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[.9fr_1.1fr] lg:items-center">
          <Reveal>
            <SectionHeading
              eyebrow="See how Smart Drain works"
              title="Catch waste Keep water moving Monitor Act early."
              description="This overview video introduces the product concept, the litter-interception logic and the role of the digital monitoring layer."
            />
          </Reveal>
          <Reveal delay={0.08}>
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-950 p-3 shadow-xl dark:border-white/10">
              <div className="relative overflow-hidden rounded-[1.5rem] bg-black">
                <video
                  className="aspect-video h-full w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster="/media/hero/hero-video-poster.jpg"
                  aria-label="See How Smart Drain Works"
                >
                  <source src="/media/hero/hero-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-100 px-6 py-24 sm:py-32 dark:bg-white/[.03]">
        <div className="mx-auto max-w-7xl">
          <Reveal><SectionHeading eyebrow="Product Types" title="Built for Different Drains. Designed for Real Conditions." description="Choose the Smart Drain format that fits your site, from kerb inlet filters to open-drain retrofit systems." /></Reveal>
          <div className="mt-12 grid gap-8">
            {products.map((product, i) => (
              <Reveal key={product.slug} delay={i * 0.06}>
                <div className="grid overflow-hidden rounded-[2rem] border border-slate-200 bg-white lg:grid-cols-[1fr_.95fr] dark:border-white/10 dark:bg-white/[.04]">
                  <div className={i % 2 ? "lg:order-2" : ""}>
                    <div className="grid h-full gap-3 p-3 sm:grid-cols-2">
                      <RemoteImage src={product.image} alt={product.title} className="min-h-[18rem] rounded-[1.5rem]" />
                      <RemoteImage src={product.secondaryImage ?? product.image} alt={`${product.title} alternate visual`} className="min-h-[18rem] rounded-[1.5rem]" />
                    </div>
                  </div>
                  <div className="p-8 sm:p-10">
                    <Badge className="text-emerald-800 dark:text-emerald-300">{product.kicker}</Badge>
                    <h2 className="mt-5 text-4xl font-semibold tracking-[-.05em]">{product.title}</h2>
                    <p className="mt-5 leading-8 text-slate-600 dark:text-slate-400">{product.description}</p>
                    <div className="mt-7 grid gap-3">
                      {product.bullets.map((bullet) => (
                        <div
                          key={bullet}
                          className="flex items-center gap-2 rounded-2xl bg-gray-100 px-4 py-3 text-sm font-semibold text-black dark:bg-gray-200 dark:text-black"
                        >
                          <span className="size-2 shrink-0 rounded-full bg-[#669c38]" />
                          {bullet}
                        </div>
                      ))}
                    </div>
                    <div className="mt-8 flex flex-wrap gap-3">
                      <Link to="/products"><Button variant="outline">Explore product details</Button></Link>
                      <Link to="/start-pilot"><Button>Start a Smart Drain Pilot <ArrowRight className="size-4" /></Button></Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#061a1d] px-6 py-24 text-white sm:py-32">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[1fr_.95fr] lg:items-center">
          <Reveal>
            <SectionHeading light eyebrow="Digital visibility" title="SEE WHAT'S HAPPENING INSIDE THE DRAIN." description="Monitor water level, filter fill and drain conditions remotely. Get early visibility, clean when needed and act before problems escalate." />
            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              {[[Gauge, "Water level"], [Radio, "Remote status"], [Globe2, "Platform-ready"]].map(([Icon, label]) => {
                const I = Icon as typeof Gauge
                return <div key={String(label)} className="rounded-2xl border border-white/10 bg-white/[.04] p-5"><I className="size-5 text-emerald-200" /><div className="mt-4 text-sm font-semibold">{String(label)}</div></div>
              })}
            </div>
            <a href={livePlatformUrl} target="_blank" rel="noreferrer" className="mt-8 inline-block"><Button>Open smartdrain.info <ExternalLink className="size-4" /></Button></a>
          </Reveal>
          <Reveal delay={0.08}><MetricDashboard /></Reveal>
        </div>
      </section>

      <section className="px-6 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal><SectionHeading eyebrow="Completed projects" title="Completed Installations" description="See Smart Drain in action across completed installations. Explore real-world deployments, field performance and how the system operates under actual drainage conditions." /></Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {[kolonnawaMedia.completedInstallation, kolonnawaMedia.clearDrain, kolonnawaMedia.iotMonitor].map((image, index) => (
              <Reveal key={image} delay={index * 0.05}><RemoteImage src={image} alt={`Kolonnawa project image ${index + 1}`} className="aspect-[4/3] rounded-[1.75rem]" /></Reveal>
            ))}
          </div>
          <Reveal delay={0.08} className="mt-10">
            <div className="grid overflow-hidden rounded-[2rem] border border-slate-200 bg-white lg:grid-cols-[1.05fr_.95fr] dark:border-white/10 dark:bg-white/[.04]">
              <RemoteImage src={deployment.heroImage ?? kolonnawaMedia.completedInstallation} alt="Completed Smart Drain project in Kolonnawa" className="h-full min-h-[24rem]" />
              <div className="p-8 sm:p-10">
                <Badge className="text-emerald-800 dark:text-emerald-300">{deployment.status}</Badge>
                <h3 className="mt-5 text-3xl font-semibold tracking-[-.045em]">{deployment.site}</h3>
                <p className="mt-4 leading-7 text-slate-600 dark:text-slate-400">{deployment.intervention}</p>
                <div className="mt-7 rounded-[1.5rem] bg-emerald-50 p-6 dark:bg-emerald-400/10">
                  <div className="text-5xl font-semibold tracking-[-.06em] text-emerald-800 dark:text-emerald-300">32 kg</div>
                  <p className="mt-2 text-sm leading-6 text-emerald-950 dark:text-emerald-100">Plastic and organic waste captured at one hotspot in a single week while maintaining stormwater flow.</p>
                </div>
                <div className="mt-7"><Link to="/deployments"><Button variant="outline">Explore completed projects <ArrowRight className="size-4" /></Button></Link></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-slate-100 px-6 py-24 sm:py-32 dark:bg-white/[.03]">
        <div className="mx-auto max-w-7xl">
          <Reveal><SectionHeading eyebrow="Outcomes" title="Smarter drainage. Better outcomes." description="Upgrade the drain, not the whole network. Smart Drain can be adapted to kerb inlets, open channels and enclosed drainage networks." /></Reveal>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {outcomes.map((item, i) => <Reveal key={item.title} delay={i * 0.05}><Card className="h-full p-6"><div className="text-3xl font-semibold text-emerald-600 dark:text-emerald-400">0{i + 1}</div><h3 className="mt-6 text-xl font-semibold">{item.title}</h3><p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-400">{item.body}</p></Card></Reveal>)}
          </div>
          <Reveal delay={0.08}><div className="mt-8 rounded-[1.75rem] border border-emerald-200 bg-emerald-50 p-6"><div className="text-sm font-semibold uppercase tracking-[.16em] text-emerald-800">SAVE UP TO 46% ON DRAIN MAINTENANCE</div><p className="mt-3 max-w-3xl text-lg font-semibold tracking-[-.02em] text-emerald-950">Smart Drain enables targeted cleaning. It supports early intervention through real-time monitoring. This reduces unnecessary inspections, maintenance effort and operational costs.</p></div></Reveal>
        </div>
      </section>

      <section className="px-6 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal><SectionHeading eyebrow="Proven innovation" title="Recognised internationally. Built for deployment." description="From patented research to live field implementation, Smart Drain is being developed and validated for cities and drainage networks worldwide." /></Reveal>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {awards.map((award, i) => (
              <Reveal key={award.title} delay={i * 0.05}>
                {award.href ? (
                  <a href={award.href} target="_blank" rel="noreferrer" className="group block h-full">
                    <Card className="h-full p-6 transition-all duration-200 hover:-translate-y-1 hover:border-emerald-500/40 hover:shadow-lg dark:hover:border-emerald-500/30">
                      <div className="flex items-start justify-between">
                        <div className="text-4xl font-semibold tracking-[-.05em] text-emerald-700 dark:text-emerald-400">{award.year}</div>
                        <ExternalLink className="size-4 text-slate-400 opacity-60 transition-opacity group-hover:opacity-100 group-hover:text-emerald-600 dark:group-hover:text-emerald-400" />
                      </div>
                      <h3 className="mt-5 font-semibold transition-colors group-hover:text-emerald-600 dark:group-hover:text-emerald-300">{award.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">{award.note}</p>
                    </Card>
                  </a>
                ) : (
                  <Card className="h-full p-6">
                    <div className="text-4xl font-semibold tracking-[-.05em] text-emerald-700 dark:text-emerald-400">{award.year}</div>
                    <h3 className="mt-5 font-semibold">{award.title}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">{award.note}</p>
                  </Card>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-100 px-6 py-24 sm:py-32 dark:bg-white/[.03]">
        <div className="mx-auto max-w-7xl">
          <Reveal><div className="grid gap-10 lg:grid-cols-[1fr_.9fr]"><SectionHeading eyebrow="Partnership ecosystem" title="Engineering, market access and climate-tech support." description="Smart Drain combines deployment partners with accelerator, investment-readiness and commercialisation support." /><div className="flex items-end lg:justify-end"><Link to="/partners"><Button variant="outline">View the partner ecosystem <ArrowRight className="size-4" /></Button></Link></div></div></Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-3">{[...partners, ...ecosystemSupport].slice(0, 6).map((partner) => <Card key={partner.name} className="p-5"><div className="text-sm font-semibold">{partner.name}</div><div className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">{partner.role}</div></Card>)}</div>
        </div>
      </section>

      <section className="px-6 pb-24 sm:pb-32">
        <Reveal className="mx-auto max-w-7xl">
          <div className="relative overflow-hidden rounded-[2.25rem] bg-emerald-300 p-9 text-slate-950 sm:p-14 dark:bg-emerald-400/15 dark:text-white">
            <div className="absolute -right-20 -top-20 size-72 rounded-full border-[42px] border-slate-950/5 dark:border-white/5" />
            <Badge>Pilot Smart Drain in your city</Badge>
            <h2 className="mt-5 max-w-4xl text-balance text-4xl font-semibold tracking-[-.055em] sm:text-6xl">Start a Smart Drain Pilot.</h2>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-slate-800/75 dark:text-white/65">Identify a drain, street, precinct or drainage corridor experiencing recurring litter, clogging, high maintenance demand or localised flooding. We will design a site-specific pilot with measurable KPIs.</p>
            <div className="mt-6 flex flex-wrap gap-2 text-sm font-semibold">{["Litter interception", "Drainage performance", "Water-level monitoring", "Maintenance", "Operational response"].map((item) => <span key={item} className="rounded-full bg-slate-950/8 px-3 py-2 dark:bg-white/10">{item}</span>)}</div>
            <div className="mt-8 flex flex-wrap gap-3"><Link to="/start-pilot"><Button className="bg-slate-950 text-white hover:bg-slate-800" size="lg">Start a Pilot <ArrowRight className="size-4" /></Button></Link><Link to="/contact"><Button variant="outline" size="lg" className="border-slate-950/20 bg-transparent hover:bg-slate-950/5 dark:border-white/20 dark:hover:bg-white/10">Contact us</Button></Link></div>
          </div>
        </Reveal>
      </section>
    </>
  )
}
